import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Table, Button, Modal, Spinner, Badge } from '@/components/ui';
import { qpTrackingTaskUrl } from '../../work-id/utils/taskPageUrl';
import { REPORT_HEADER_BG } from '../utils/reportTable';

export interface TaskDrillRow {
  taskId: number;
  taskNumber: string | null;
  taskName: string | null;
  status: string;
  value: number;
  accomplishment?: number | null;
  date: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  FINALIZED: 'Finalized',
  CLOSED: 'Closed',
  INTERNAL_REVIEW: 'Internal Review',
  PLANNED: 'Planned',
  PROPOSED: 'Proposed',
  ACTIVE: 'Active',
  ACTIVE_MARKED_AS_COMPLETE: 'Active (Marked Complete)',
};

const STATUS_VARIANT: Record<string, string> = {
  FINALIZED: 'success',
  CLOSED: 'secondary',
  INTERNAL_REVIEW: 'info',
  PLANNED: 'primary',
  PROPOSED: 'light',
  ACTIVE: 'warning',
  ACTIVE_MARKED_AS_COMPLETE: 'warning',
};

const formatMoney = (value: number) => '$' + Math.round(value).toLocaleString('en-US');
const formatQty = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface TaskDrillModalProps {
  show: boolean;
  onHide: () => void;
  /** e.g. "Tasks – (#101) Patching" */
  title: React.ReactNode;
  /** Context line under the title (month, range, selection); task count and total are appended. */
  subtitle: React.ReactNode;
  tasks: TaskDrillRow[];
  loading: boolean;
  error: string | null;
  /** Show the accomplishment column, labelled with this unit (omit to hide the column). */
  quantityUnit?: string | null;
}

/**
 * The task list behind a clicked report cell: task number (links to QP Tracking), name, status,
 * value, optional quantity and date, with a total row when more than one task contributes.
 * Shared by Quantified Value and QBS Summary so drill-downs look and behave the same.
 */
const TaskDrillModal: React.FC<TaskDrillModalProps> = ({
  show,
  onHide,
  title,
  subtitle,
  tasks,
  loading,
  error,
  quantityUnit
}) => {
  const showQuantity = quantityUnit !== undefined;
  const totalValue = tasks.reduce((sum, t) => sum + t.value, 0);
  const ready = !loading && !error;

  return (
    // autoHeight: size to the task list (capped + scrollable) instead of the fixed 85vh form height.
    <Modal show={show} onHide={onHide} size="lg" centered autoHeight>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>
          {title}
          <div className="tw-text-content-muted tw-font-normal" style={{ fontSize: '0.85rem' }}>
            {subtitle}
            {ready && tasks.length > 0 && (
              <>
                {' · '}
                {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
                {' · '}
                {formatMoney(totalValue)}
              </>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="tw-flex tw-justify-center tw-py-4">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        {error && <Alert variant="danger">{error}</Alert>}
        {ready && tasks.length === 0 && (
          <p className="tw-text-content-muted tw-text-center tw-py-3">No tasks found for this period.</p>
        )}
        {ready && tasks.length > 0 && (
          <Table striped bordered hover size="sm" style={{ fontSize: '0.875rem', marginBottom: 0 }}>
            <thead>
              <tr style={{ backgroundColor: REPORT_HEADER_BG }}>
                <th title="Task ID">Task ID</th>
                <th title="Name">Name</th>
                <th title="Status">Status</th>
                {/* Short labels: the shared Table keeps headers on one uppercase line, so long ones set
                    the table's minimum width and push the lg modal into a horizontal scroll. */}
                <th style={{ textAlign: 'right' }} title="Value ($)">Value</th>
                {showQuantity && (
                  <th style={{ textAlign: 'right' }} title={`Accomplishment${quantityUnit ? ` (${quantityUnit})` : ''}`}>
                    Qty{quantityUnit && ` (${quantityUnit})`}
                  </th>
                )}
                <th title="Date">Date</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.taskId}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link to={qpTrackingTaskUrl(task.taskId)} title="Open task" className="tw-text-primary tw-underline">
                      {task.taskNumber || task.taskId}
                    </Link>
                  </td>
                  <td>{task.taskName || '–'}</td>
                  <td>
                    <Badge bg={STATUS_VARIANT[task.status] || 'secondary'} style={{ fontSize: '0.75rem', whiteSpace: 'normal' }}>
                      {STATUS_LABELS[task.status] || task.status}
                    </Badge>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {task.value !== 0 ? formatMoney(task.value) : '-'}
                  </td>
                  {showQuantity && (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {task.accomplishment != null && task.accomplishment !== 0 ? formatQty(task.accomplishment) : '-'}
                    </td>
                  )}
                  <td style={{ whiteSpace: 'nowrap' }}>{task.date || '–'}</td>
                </tr>
              ))}
            </tbody>
            {/* A total row only adds information when there is more than one task to sum. */}
            {tasks.length > 1 && (
              <tfoot>
                <tr style={{ fontWeight: 'bold', backgroundColor: REPORT_HEADER_BG }}>
                  <td colSpan={3}>Total</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMoney(totalValue)}</td>
                  {showQuantity && (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const total = tasks.reduce((s, t) => s + (t.accomplishment ?? 0), 0);
                        return total !== 0 ? formatQty(total) : '-';
                      })()}
                    </td>
                  )}
                  <td></td>
                </tr>
              </tfoot>
            )}
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskDrillModal;
